// hh-ki1 (case-29) — UndoScreen.
//
// Connected full-screen undo flow. Reached when the user taps «Отменить»
// from the dashboard last-activity strip (router.go('undo')) or from an
// assistant summary bubble in the chat (router.go('undo', { id })).
//
// Flow (case-29): превью того, что будет отменено → подтверждение → откат.
// Окно отката — 24 часа (ChangeRecord.ExpiresAt).
//
// Data: GET /api/v1/changes (universal change journal, Phase 1.5.3) — every
// mutation, human or AI, produces a ChangeRecord through MutationService.
//   • with `id` param  → GET /api/v1/changes/{id}      (a specific change)
//   • without          → GET /api/v1/changes?limit=20  (freshest undoable)
// Undo: POST /api/v1/changes/{id}/undo — 409 drift, 410 expired/done.
//
// The snapshot is a kind-tagged JSON blob; describeChange() parses it
// defensively into a human preview without assuming every field is present.

import type { JSX } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { T, Icon, Avatar, Btn, type IconName } from '../../tokens';
import { ScreenHeader } from '../../shell';
import type { RouterApi } from '../../router';
import { undo as changes, ApiError, humanizeError } from '../../api';
import type { ChangeRecordRow } from '../../api/endpoints/undo.js';

export interface UndoScreenProps {
  readonly router: RouterApi;
}

// ─── Preview model ───────────────────────────────────────────────────────

interface ChangePreview {
  readonly icon: IconName;
  /** What the undo will do, e.g. "Вернуть вещь на прежнее место". */
  readonly verb: string;
  /** Optional detail line — entity names / counts. */
  readonly detail: string;
}

const ENTITY_LABEL: Record<string, string> = {
  item: 'вещь',
  equipment: 'оборудование',
  room: 'комната',
  property: 'собственность',
  schedule_rule: 'пункт регламента',
};

function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `${n} ${one}`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return `${n} ${few}`;
  return `${n} ${many}`;
}

/**
 * Parses ChangeRecord.snapshotJson into a human preview. The snapshot shape
 * is tagged by `kind` (create/move/update/delete/destruct) — see
 * MutationService.cs. Undo reverses the change, so the verbs describe the
 * reversal, not the original action.
 */
function describeChange(row: ChangeRecordRow): ChangePreview {
  let snap: Record<string, unknown> = {};
  try {
    const parsed: unknown = JSON.parse(row.snapshotJson);
    if (parsed !== null && typeof parsed === 'object') {
      snap = parsed as Record<string, unknown>;
    }
  } catch {
    // Malformed snapshot — fall through to the kind-only description below.
  }

  const entityType = typeof snap['entityType'] === 'string' ? (snap['entityType'] as string) : '';
  const entityLabel = ENTITY_LABEL[entityType] ?? 'запись';
  const entity = (snap['entity'] ?? {}) as Record<string, unknown>;
  const entityName = typeof entity['name'] === 'string' ? (entity['name'] as string) : '';

  switch (row.kind) {
    case 'create': {
      const ids = Array.isArray(snap['ids']) ? (snap['ids'] as unknown[]) : [];
      return {
        icon: 'trash',
        verb: 'Удалить созданное',
        detail: plural(ids.length || 1, 'запись', 'записи', 'записей'),
      };
    }
    case 'move': {
      const items = Array.isArray(snap['items']) ? (snap['items'] as unknown[]) : [];
      return {
        icon: 'move',
        verb: 'Вернуть на прежнее место',
        detail: plural(items.length || 1, 'вещь', 'вещи', 'вещей'),
      };
    }
    case 'update': {
      return {
        icon: 'undo',
        verb: 'Вернуть прежние значения',
        detail: entityName.length > 0 ? entityName : entityLabel,
      };
    }
    case 'delete':
    case 'destruct': {
      return {
        icon: 'history',
        verb: 'Восстановить удалённое',
        detail: entityName.length > 0 ? `${entityLabel}: ${entityName}` : entityLabel,
      };
    }
    default:
      return { icon: 'undo', verb: 'Откатить изменение', detail: '' };
  }
}

// ─── Time helpers ────────────────────────────────────────────────────────

function useNow(intervalMs = 30_000): number {
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
}

function formatRemaining(msLeft: number): string {
  if (msLeft <= 0) return 'окно отмены закрыто';
  const totalMin = Math.floor(msLeft / 60_000);
  const hours = Math.floor(totalMin / 60);
  const minutes = totalMin % 60;
  if (hours > 0) return `до закрытия окна ${hours} ч ${minutes} мин`;
  return `до закрытия окна ${minutes} мин`;
}

/** True when the change can still be reversed. */
function isUndoable(row: ChangeRecordRow, now: number): boolean {
  if (row.undoneAt !== null) return false;
  const expires = Date.parse(row.expiresAt);
  return Number.isNaN(expires) || expires > now;
}

/** Maps an undo ApiError onto a human message (see ChangesController). */
function describeUndoError(err: unknown): string {
  return humanizeError(err, 'delete');
}

// ─── State ───────────────────────────────────────────────────────────────

type Phase =
  | { readonly kind: 'loading' }
  | { readonly kind: 'error'; readonly message: string }
  | { readonly kind: 'empty' }
  | { readonly kind: 'ready'; readonly change: ChangeRecordRow }
  | { readonly kind: 'undoing'; readonly change: ChangeRecordRow }
  | { readonly kind: 'failed'; readonly change: ChangeRecordRow; readonly message: string }
  | { readonly kind: 'done' };

// ─── Component ───────────────────────────────────────────────────────────

export function UndoScreen({ router }: UndoScreenProps): JSX.Element {
  const idParam = router.top?.params['id'];
  const changeId = typeof idParam === 'string' ? idParam : null;

  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const now = useNow();

  useEffect(() => {
    let cancelled = false;
    setPhase({ kind: 'loading' });

    const load = async (): Promise<Phase> => {
      if (changeId !== null) {
        const change = await changes.getChange(changeId);
        return { kind: 'ready', change };
      }
      // No specific id — undo the freshest still-reversible change.
      const list = await changes.listChanges({ limit: 20 });
      const fresh = list.changes.find((c) => isUndoable(c, Date.now()));
      return fresh !== undefined ? { kind: 'ready', change: fresh } : { kind: 'empty' };
    };

    load()
      .then((next) => {
        if (!cancelled) setPhase(next);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message =
          err instanceof ApiError && err.status === 404
            ? 'Изменение не найдено.'
            : humanizeError(err, 'load');
        setPhase({ kind: 'error', message });
      });
    return () => {
      cancelled = true;
    };
  }, [changeId]);

  const runUndo = (change: ChangeRecordRow): void => {
    setPhase({ kind: 'undoing', change });
    changes
      .undoChange(change.id)
      .then(() => setPhase({ kind: 'done' }))
      .catch((err: unknown) => {
        setPhase({ kind: 'failed', change, message: describeUndoError(err) });
      });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: T.bg }}>
      <ScreenHeader
        title="Отменить действие"
        subtitle="Окно отката — 24 часа"
        onBack={router.back}
      />
      <div style={{ flex: 1, overflow: 'auto', padding: '4px 16px 24px' }}>
        {phase.kind === 'loading' && <Note text="Загружаю…" />}
        {phase.kind === 'error' && <Note text={phase.message} tone="danger" />}
        {phase.kind === 'empty' && (
          <Note text="Нечего отменять — за последние сутки изменений нет." />
        )}
        {phase.kind === 'done' && (
          <DoneCard onBack={router.back} />
        )}
        {(phase.kind === 'ready' ||
          phase.kind === 'undoing' ||
          phase.kind === 'failed') && (
          <UndoBody
            change={phase.change}
            now={now}
            pending={phase.kind === 'undoing'}
            error={phase.kind === 'failed' ? phase.message : null}
            onConfirm={() => runUndo(phase.change)}
            onCancel={router.back}
          />
        )}
      </div>
    </div>
  );
}

// ─── Undo body (preview + confirm) ───────────────────────────────────────

interface UndoBodyProps {
  readonly change: ChangeRecordRow;
  readonly now: number;
  readonly pending: boolean;
  readonly error: string | null;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

function UndoBody({
  change,
  now,
  pending,
  error,
  onConfirm,
  onCancel,
}: UndoBodyProps): JSX.Element {
  const preview = useMemo(() => describeChange(change), [change]);
  const expiresMs = Date.parse(change.expiresAt);
  const msLeft = Number.isNaN(expiresMs) ? Number.POSITIVE_INFINITY : expiresMs - now;
  const closed = !isUndoable(change, now);
  const actor = change.actorKind === 'ai' ? 'ассистент' : 'вручную';

  return (
    <>
      <div
        style={{
          background: T.brandSoft,
          border: `1px solid ${T.brandSoft2}`,
          borderRadius: 16,
          padding: 16,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar kind="ai" icon="undo" size={36} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.brandDeep }}>
              Что будет отменено
            </div>
            <div style={{ fontSize: 12, color: T.ink2, marginTop: 2 }}>
              изменение · {actor} ·{' '}
              {Number.isFinite(msLeft) ? formatRemaining(msLeft) : 'окно открыто'}
            </div>
          </div>
        </div>

        <div
          style={{
            marginTop: 14,
            padding: 12,
            background: 'rgba(255,255,255,0.6)',
            borderRadius: 10,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <Icon name={preview.icon} size={16} color={T.brandDeep} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.ink }}>{preview.verb}</div>
            {preview.detail.length > 0 && (
              <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 1 }}>{preview.detail}</div>
            )}
          </div>
        </div>
      </div>

      {error !== null && <Note text={error} tone="danger" />}

      {closed ? (
        <div
          style={{
            marginTop: 16,
            padding: '14px 16px',
            borderRadius: 12,
            background: T.surfaceAlt,
            border: `1px solid ${T.border}`,
            color: T.ink3,
            fontSize: 13,
            fontWeight: 600,
            textAlign: 'center',
          }}
        >
          {change.undoneAt !== null ? 'Уже откатили' : 'Окно отмены закрыто'}
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <Btn kind="ghost" onClick={onCancel} style={{ flex: 1 }} disabled={pending}>
            Не надо
          </Btn>
          <Btn
            kind="primary"
            icon="undo"
            onClick={onConfirm}
            style={{ flex: 1.4 }}
            disabled={pending}
          >
            {pending ? 'Откатываю…' : 'Откатить'}
          </Btn>
        </div>
      )}
    </>
  );
}

// ─── Done card ───────────────────────────────────────────────────────────

function DoneCard({ onBack }: { readonly onBack: () => void }): JSX.Element {
  return (
    <div
      style={{
        marginTop: 8,
        background: T.surface,
        border: `1px solid ${T.border}`,
        borderRadius: 16,
        padding: 20,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12,
        textAlign: 'center',
      }}
    >
      <Avatar kind="ai" icon="check" size={40} />
      <div style={{ fontSize: 15, fontWeight: 700, color: T.ink }}>Откатил</div>
      <div style={{ fontSize: 12.5, color: T.ink3, lineHeight: 1.4 }}>
        Состояние вернулось к тому, что было до изменения.
      </div>
      <Btn kind="primary" onClick={onBack} style={{ marginTop: 4 }}>
        Готово
      </Btn>
    </div>
  );
}

// ─── Note ────────────────────────────────────────────────────────────────

function Note({ text, tone }: { readonly text: string; readonly tone?: 'danger' }): JSX.Element {
  return (
    <div
      style={{
        padding: '28px 20px',
        textAlign: 'center',
        fontSize: 13,
        fontWeight: 500,
        color: tone === 'danger' ? T.danger : T.ink3,
        lineHeight: 1.45,
      }}
    >
      {text}
    </div>
  );
}
